import type { TFeedIdParam } from '@/controllers/feed/feed.types';
import { db } from '@/database/franky/franky';
import { DatabaseErrorCode } from '@/libs/error/error.codes';
import { Err } from '@/libs/error/error.factories';
import { ErrorService } from '@/libs/error/error.types';
import { isAppError } from '@/libs/error/error.utils';
import { FeedModel } from '@/models/feed/feed';
import type { FeedModelSchema } from '@/models/feed/feed.schema';
import type { TFeedReconcileParams, TFeedReconcileResult, TFeedRollbackParams, TFeedWriteResult } from './feed.types';

const FEED_TABLES = [FeedModel.table];

export class LocalFeedService {
  private constructor() {}

  private static normalize(feed: FeedModelSchema): FeedModelSchema {
    return { ...feed, domain_tags: feed.domain_tags ?? [] };
  }

  /**
   * Persist a feed to local storage.
   * The ID is always a HashId-derived string provided upfront, so this is a plain upsert. The row as
   * it was before the write is read in the same transaction and returned alongside, so a caller that
   * has to undo the write holds an exact snapshot.
   */
  static async createOrUpdate(feed: FeedModelSchema): Promise<TFeedWriteResult> {
    return await db.transaction('rw', FEED_TABLES, async () => {
      const prior = await FeedModel.findById(feed.id);
      await FeedModel.upsert(feed);
      return {
        persisted: this.normalize(await FeedModel.findByIdOrThrow(feed.id)),
        prior: prior ? this.normalize(prior) : null,
      };
    });
  }

  static async delete({ feedId }: TFeedIdParam) {
    await db.transaction('rw', FEED_TABLES, async () => {
      await FeedModel.deleteById(feedId);
    });
  }

  /**
   * Replaces the local feeds with what the homeserver listing holds, in one transaction: upserts
   * `feeds`, deletes every row whose id is not in `keepIds`, and returns the rows removed so the
   * caller can drop their stream caches. The listing is the source of truth for which feeds exist:
   * a row it no longer names was deleted elsewhere, or carries a 0.x id from before the account was
   * migrated. `isCurrent` is checked inside the transaction, so a listing that finished after an
   * account switch writes nothing for the next account (`applied: false`).
   */
  static async reconcile({ feeds, keepIds, isCurrent }: TFeedReconcileParams): Promise<TFeedReconcileResult> {
    try {
      return await db.transaction('rw', FEED_TABLES, async () => {
        if (isCurrent && !isCurrent()) return { applied: false, persisted: [], removed: [] };
        if (feeds.length > 0) await FeedModel.bulkSave(feeds);
        const stale = (await FeedModel.table.toArray()).filter((feed) => !keepIds.has(feed.id));
        if (stale.length > 0) await FeedModel.table.bulkDelete(stale.map((feed) => feed.id));
        return {
          applied: true,
          persisted: feeds.map((feed) => this.normalize(feed)),
          removed: stale.map((feed) => this.normalize(feed)),
        };
      });
    } catch (error) {
      if (isAppError(error)) throw error;
      throw Err.database(
        DatabaseErrorCode.TRANSACTION_FAILED,
        'Failed to reconcile feeds with the homeserver listing',
        {
          service: ErrorService.Local,
          operation: 'reconcile',
          context: { table: FeedModel.table.name, count: feeds.length },
          cause: error,
        },
      );
    }
  }

  /**
   * Undo a local write that failed to sync. Restores `priorFeed` (or deletes the row when the
   * write created it) only while the row still carries the write's own `updated_at`: a newer
   * write to the same id (another tab, a bootstrap fetch landing mid-sync) is left alone, so a
   * successful edit is never reverted. Returns whether the row was changed.
   */
  static async rollback({ feedId, expectedUpdatedAt, priorFeed }: TFeedRollbackParams): Promise<boolean> {
    return await db.transaction('rw', FEED_TABLES, async () => {
      const current = await FeedModel.findById(feedId);
      if (!current || current.updated_at !== expectedUpdatedAt) return false;
      if (priorFeed) {
        await FeedModel.upsert(priorFeed);
      } else {
        await FeedModel.deleteById(feedId);
      }
      return true;
    });
  }

  /**
   * Read a feed by ID. Returns `null` when the feed does not exist locally.
   *
   * History: this used to throw RECORD_NOT_FOUND ("Feed not found",
   * Sentry PUBKY-APP-7A). The two production callers (`useCustomFeed`,
   * stream coordinator resolution) both handle a missing feed as a normal
   * state — a stale `activeFeedId` pointing at a deleted feed — so a throw
   * produced pure Sentry noise and a caught-error path that returned
   * `undefined` anyway. `find()`/`get()` semantics are the correct contract.
   */
  static async read({ feedId }: TFeedIdParam): Promise<FeedModelSchema | null> {
    const record = await FeedModel.findById(feedId);
    return record ? this.normalize(record) : null;
  }

  static async readAll(): Promise<FeedModelSchema[]> {
    return (await FeedModel.findAllSorted()).map((feed) => this.normalize(feed));
  }
}
